import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import {
  tvImportListsApi,
  type CreateTvImportListRequest,
  type TvImportList,
  type TvImportListKind,
  type TvMonitorPreset,
} from "@/api/tvImportLists";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Checkbox, Field, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";

/**
 * TV → Import lists. Closes the Sonarr-onboarding gap (issue #72):
 * point at a Trakt watchlist of shows and auto-add monitored series
 * on a recurring schedule. Mirrors the movie-side
 * /movies/import-lists page so the two have one mental model.
 *
 * The per-list `monitor_preset` is the only user-visible difference
 * vs movies — we surface the same preset enum the Add Series modal
 * uses (#71) so library-bootstrap and one-off adds behave the same.
 */

type EditingState =
  | { mode: "closed" }
  | { mode: "create" }
  | { mode: "edit"; row: TvImportList };

const KIND_LABEL: Record<TvImportListKind, string> = {
  "trakt-list": "Trakt",
};

const KIND_HINT: Record<TvImportListKind, string> = {
  "trakt-list": "user/slug — e.g. justin/sonarr-watchlist",
};

const PRESET_LABEL: Record<TvMonitorPreset, string> = {
  all: "All episodes",
  future: "Future episodes only",
  missing: "Only missing episodes",
  existing: "Only already-imported episodes",
  "first-season": "First season only",
  "last-season": "Last season only",
  none: "Nothing (added unmonitored)",
};

const PRESET_OPTIONS: TvMonitorPreset[] = [
  "future",
  "all",
  "missing",
  "existing",
  "first-season",
  "last-season",
  "none",
];

export function TVImportLists() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<EditingState>({ mode: "closed" });
  const [syncingId, setSyncingId] = useState<number | null>(null);

  const { data: rows, isLoading } = useQuery({
    queryKey: ["tv-import-lists"],
    queryFn: tvImportListsApi.list,
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => tvImportListsApi.remove(id),
    onSuccess: () => {
      toast("Import list removed", "success");
      qc.invalidateQueries({ queryKey: ["tv-import-lists"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Remove failed", "error"),
  });

  const toggleEnabled = async (row: TvImportList) => {
    try {
      await tvImportListsApi.update(row.id, { enabled: !row.enabled });
      qc.invalidateQueries({ queryKey: ["tv-import-lists"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Update failed", "error");
    }
  };

  const syncNow = async (row: TvImportList) => {
    setSyncingId(row.id);
    try {
      const outcome = await tvImportListsApi.syncNow(row.id);
      const summary =
        `seen ${outcome.total_seen}, added ${outcome.added}` +
        (outcome.unresolved ? `, ${outcome.unresolved} unresolved` : "") +
        (outcome.failed ? `, ${outcome.failed} failed` : "");
      toast(
        `"${row.name}" synced — ${summary}`,
        outcome.added > 0 ? "success" : "info",
      );
      qc.invalidateQueries({ queryKey: ["tv-import-lists"] });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Sync failed", "error");
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="TV Import Lists"
        description="Sync your library against Trakt show watchlists. Series on the upstream list are auto-added with the configured monitor preset; fresh additions are picked up on every sync."
        actions={
          <Button onClick={() => setEditing({ mode: "create" })}>
            New import list
          </Button>
        }
      />

      {isLoading ? (
        <p className="text-sm text-foreground/60">Loading…</p>
      ) : !rows || rows.length === 0 ? (
        <EmptyState
          title="No import lists"
          description="Add a Trakt show watchlist to bulk-populate your library on a recurring schedule."
          action={
            <Button onClick={() => setEditing({ mode: "create" })}>
              New import list
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded border border-line">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/50 text-left text-xs uppercase text-foreground/60">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Kind</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2 font-medium">Monitor</th>
                <th className="px-4 py-2 font-medium">Last sync</th>
                <th className="px-4 py-2 font-medium">Hits</th>
                <th className="px-4 py-2 font-medium">Enabled</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-4 py-2 font-medium">{row.name}</td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {KIND_LABEL[row.kind]}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-foreground/70">
                    {row.source_url_or_id}
                  </td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {PRESET_LABEL[row.monitor_preset]}
                  </td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {row.last_synced_at
                      ? new Date(row.last_synced_at).toLocaleString()
                      : "—"}
                  </td>
                  <td className="px-4 py-2 text-xs text-foreground/70">
                    {row.hit_count}
                  </td>
                  <td className="px-4 py-2">
                    <Checkbox
                      checked={row.enabled}
                      onChange={() => toggleEnabled(row)}
                    />
                  </td>
                  <td className="space-x-2 px-4 py-2 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={syncingId === row.id}
                      onClick={() => syncNow(row)}
                    >
                      Sync now
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setEditing({ mode: "edit", row })}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        if (confirm(`Remove import list "${row.name}"?`)) {
                          removeMut.mutate(row.id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ImportListModal
        editing={editing}
        onClose={() => setEditing({ mode: "closed" })}
      />
    </div>
  );
}

interface FormState {
  name: string;
  kind: TvImportListKind;
  source_url_or_id: string;
  monitor_preset: TvMonitorPreset;
  enabled: boolean;
}

const blankForm = (): FormState => ({
  name: "",
  kind: "trakt-list",
  source_url_or_id: "",
  monitor_preset: "future",
  enabled: true,
});

const formFromRow = (row: TvImportList): FormState => ({
  name: row.name,
  kind: row.kind,
  source_url_or_id: row.source_url_or_id,
  monitor_preset: row.monitor_preset,
  enabled: row.enabled,
});

const toBody = (f: FormState): CreateTvImportListRequest => ({
  name: f.name.trim(),
  kind: f.kind,
  source_url_or_id: f.source_url_or_id.trim(),
  monitor_preset: f.monitor_preset,
  enabled: f.enabled,
});

function ImportListModal({
  editing,
  onClose,
}: {
  editing: EditingState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>(blankForm());
  const [submitting, setSubmitting] = useState(false);

  const open = editing.mode !== "closed";
  const isEdit = editing.mode === "edit";
  const editingRow = editing.mode === "edit" ? editing.row : null;

  useEffect(() => {
    if (editing.mode === "create") setForm(blankForm());
    if (editing.mode === "edit") setForm(formFromRow(editing.row));
  }, [editing]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = toBody(form);
      if (!body.name) {
        toast("Name is required", "error");
        return;
      }
      if (!body.source_url_or_id) {
        toast("Source is required", "error");
        return;
      }
      if (isEdit && editingRow) {
        await tvImportListsApi.update(editingRow.id, body);
        toast("Import list updated", "success");
      } else {
        await tvImportListsApi.create(body);
        toast("Import list created", "success");
      }
      qc.invalidateQueries({ queryKey: ["tv-import-lists"] });
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Save failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isEdit
          ? `Edit ${editingRow?.name ?? "import list"}`
          : "New import list"
      }
      width="md"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <TextInput
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. My Trakt watchlist"
          />
        </Field>

        <Field label="Provider">
          <Select
            value={form.kind}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                kind: e.target.value as TvImportListKind,
              }))
            }
          >
            <option value="trakt-list">Trakt — public list</option>
          </Select>
        </Field>

        <Field label="Source" hint={KIND_HINT[form.kind]}>
          <TextInput
            required
            value={form.source_url_or_id}
            onChange={(e) =>
              setForm((f) => ({ ...f, source_url_or_id: e.target.value }))
            }
            placeholder={KIND_HINT[form.kind]}
          />
        </Field>

        <Field
          label="Monitor preset"
          hint="Applied to every series this list adds — same presets as the Add Series modal."
        >
          <Select
            value={form.monitor_preset}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                monitor_preset: e.target.value as TvMonitorPreset,
              }))
            }
          >
            {PRESET_OPTIONS.map((p) => (
              <option key={p} value={p}>
                {PRESET_LABEL[p]}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.enabled}
              onChange={(e) =>
                setForm((f) => ({ ...f, enabled: e.target.checked }))
              }
            />
            <span>Enabled (auto-sync hourly)</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 border-t border-line/60 pt-4">
          <Button variant="secondary" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? "Save" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
